import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Nutrition.svg";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";
import RondleidingForm from "../components/RondleidingForm";

const Nutrition: React.FC = () => {
  const { t } = useLanguage();
  const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);

  const data = t("nutrition");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[280px] w-full opacity-100"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-4 max-w-[200px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[290px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {data.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-center text-neutral-800 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {data.hero.subtitle}
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <div className="mb-6">
                <h2 className="text-3xl font-semibold text-black mb-6 leading-tight">
                  {data.intro.title}
                </h2>
                <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                  {data.intro.text1}
                </p>
                <p className="text-lg text-neutral-800 leading-relaxed">
                  {data.intro.text2}
                </p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-100">
                <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                  {data.intro.supportTitle}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {data.intro.supportText}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Wheel of Five Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {data.wheelOfFive.title}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {data.wheelOfFive.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {data.wheelOfFive.items.map((category: any, index: number) => (
              <div key={index} className="group">
                <div
                  className={`${category.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="text-5xl mb-4">{category.icon}</div>
                  <h3 className="text-lg font-semibold mb-2">
                    {category.category}
                  </h3>
                  <p className="text-sm opacity-90">{category.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mealtime Learning Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {data.mealtime.title}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {data.mealtime.subtitle}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {data.mealtime.items.map((learning: any, index: number) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <div className="text-3xl">{learning.icon}</div>
                    <h3 className="text-xl font-semibold text-neutral-900">
                      {learning.title}
                    </h3>
                  </div>
                  <p className="text-neutral-800 leading-relaxed">
                    {learning.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="bg-[#492A19] rounded-3xl shadow-2xl p-8 text-white text-center">
              <p className="text-lg opacity-90">{data.mealtime.note}</p>
            </div>
          </div>
        </div>

        {/* Baby Nutrition Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {data.baby.title}
            </h2>
            <p className="text-xl text-neutral-900 max-w-3xl mx-auto">
              {data.baby.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {data.baby.items.map((nutrition: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <div className="text-3xl">{nutrition.icon}</div>
                  <h3 className="text-xl font-semibold text-neutral-900">
                    {nutrition.title}
                  </h3>
                </div>
                <p className="text-neutral-800 leading-relaxed">
                  {nutrition.description}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-900 max-w-4xl mx-auto">
              {data.baby.note}
            </p>
          </div>
        </div>

        {/* Toddler Meals Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-black mb-4">
              {data.toddler.title}
            </h2>
            <p className="text-xl text-neutral-900 max-w-3xl mx-auto">
              {data.toddler.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-12">
            {data.toddler.items.map((meal: any, index: number) => (
              <div key={index} className="group">
                <div
                  className={`${meal.color} rounded-2xl p-6 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="text-4xl mb-4">{meal.icon}</div>
                  <h3 className="text-lg font-semibold mb-2">{meal.title}</h3>
                  <p className="text-sm opacity-90">{meal.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-semibold text-black mb-6 text-center">
                {data.toddler.hotMealsTitle}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {data.toddler.hotMealFeatures.map(
                  (feature: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#492A19] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-neutral-900 text-left">{feature}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Independence Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-black mb-4">
              {data.independence.title}
            </h2>
            <p className="text-xl text-neutral-900 max-w-3xl mx-auto">
              {data.independence.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {data.independence.items.map((activity: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="text-4xl mb-4">{activity.icon}</div>
                <h3 className="text-xl font-semibold text-black mb-3">
                  {activity.title}
                </h3>
                <p className="text-neutral-900">{activity.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-900 max-w-4xl mx-auto">
              {data.independence.note}
            </p>
          </div>
        </div>

        {/* Cultural Sensitivity Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-black mb-4">
              {data.cultural.title}
            </h2>
            <p className="text-xl text-neutral-900 max-w-3xl mx-auto">
              {data.cultural.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {data.cultural.items.map((sensitivity: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="text-4xl mb-4">{sensitivity.icon}</div>
                <h3 className="text-xl font-semibold text-black mb-3">
                  {sensitivity.title}
                </h3>
                <p className="text-neutral-900">{sensitivity.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Educational Value Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-semibold text-black mb-4">
              {data.educational.title}
            </h2>
            <p className="text-xl text-neutral-900 max-w-3xl mx-auto">
              {data.educational.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {data.educational.items.map((activity: any, index: number) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="text-4xl mb-4">{activity.icon}</div>
                <h3 className="text-lg font-semibold text-black mb-3">
                  {activity.title}
                </h3>
                <p className="text-sm text-neutral-900">{activity.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-900 max-w-4xl mx-auto">
              {data.educational.note}
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-black mb-4">
                {data.cta.title}
              </h3>
              <p className="text-xl text-neutral-900 mb-8 max-w-2xl mx-auto leading-relaxed">
                {data.cta.subtitle}
              </p>
              <button
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
                onClick={() => setOpenForm("rondleiding")}
              >
                {t("about.scheduleVisit")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-4 right-4 text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default Nutrition;
